<!--@format-->
<template>
    <div class="link-opportunity-popup">
        <h1 class="title">
            {{ steps[step].title }}
        </h1>
        <section v-if="step === 'contacts'">
            <section class="opportunity-participants">
                <div
                    v-if="contact"
                    :class="['participant', { selected: contactSelected }]"
                    @click="toggleCheckbox('contactSelected', 'contactCheckbox')"
                >
                    <div class="details-container">
                        <div class="checkbox-wrapper">
                            <app-checkbox ref="contactCheckbox" v-model="contactSelected" />
                        </div>
                        <app-profile-image
                            :src="contact.ProfileImageUrl | profileImagePlaceholder"
                        />
                        <div class="details">
                            <span class="bold">{{
                                contact.FirstName + ' ' + contact.LastName
                            }}</span>
                            <br />
                            Contact
                        </div>
                    </div>
                </div>
                <div
                    v-if="salesperson"
                    :class="['participant', { selected: salespersonSelected }]"
                    @click="toggleCheckbox('salespersonSelected', 'salespersonCheckbox')"
                >
                    <div class="details-container">
                        <div class="checkbox-wrapper">
                            <app-checkbox ref="salespersonCheckbox" v-model="salespersonSelected" />
                        </div>
                        <app-profile-image
                            :src="salesperson.ProfileImageUrl | profileImagePlaceholder"
                        />
                        <div class="details">
                            <span class="bold">{{
                                salesperson.FirstName + ' ' + salesperson.LastName
                            }}</span>
                            <br />
                            Salesperson
                        </div>
                    </div>
                </div>
            </section>
            <section class="popup-details">
                <p>
                    These contacts are associated to
                    <span class="bold">{{ element.options.name }}</span
                    >. Would you like to add these contacts as recipients for this document?
                </p>
            </section>
        </section>
        <section v-else-if="step === 'stage'" class="opportunity-stage-wrapper">
            <div :class="['opportunity-stage', { selected: isChangingStageOnCompletion }]">
                <div class="app-switch-wrapper" @click="toggleAppSwitch">
                    <app-switch
                        ref="appSwitch"
                        :value="isChangingStageOnCompletion"
                        size="large"
                        @input="showOpportunityStages"
                    >
                    </app-switch>
                </div>
                <div class="stage-description">
                    <p @click="toggleAppSwitch">
                        When all recipients sign this document, and it is complete, change the
                        opportunity stage?
                    </p>
                    <div class="select-opportunity-stage">
                        <label>Select an opportunity stage</label>
                        <opportunity-status-dropdown
                            v-model="opportunityStatus"
                            :allow-manage="false"
                            :items="element.opportunityStatuses"
                            :client-id="element.clientId"
                        />
                    </div>
                </div>
            </div>
        </section>
        <section class="app-popup-actions inner-actions">
            <app-button
                v-if="step === 'stage'"
                class="tertiary-btn"
                @click="steps[step].secondaryBtn.action"
            >
                <span class="fa fa-arrow-left" />
                {{ steps[step].secondaryBtn.label }}
            </app-button>
            <app-button
                :class="['main-btn', { single: step === 'contacts' }]"
                @click="steps[step].mainBtn.action"
            >
                {{ steps[step].mainBtn.label }}
            </app-button>
        </section>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const AppCheckBox = () => import('@/components/common/app-checkbox');
const AppProfileImage = () => import('@/components/common/app-profile-image');
const AppSwitch = () => import('@/components/common/app-switch');
const OpportunityStatusDropdown = () =>
    import('@/components/opportunity/opportunity-board/board-opportunity-status-dropdown');

import EventBus from '@/scripts/event-bus';

export default {
    name: 'link-opportunity-popup',

    components: {
        'app-button': AppButton,
        'app-checkbox': AppCheckBox,
        'app-profile-image': AppProfileImage,
        'app-switch': AppSwitch,
        'opportunity-status-dropdown': OpportunityStatusDropdown,
    },

    props: {
        element: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            contactSelected: false,
            salespersonSelected: false,
            isChangingStageOnCompletion: false,
            opportunityStatus: null,
            recipientsAdded: [],
            step: 'contacts',
            steps: {
                contacts: {
                    title: 'Add these contacts as recipients for this document?',
                    // secondaryBtn: {
                    //     label: 'No Thanks',
                    //     action: () => {
                    //         this.recipientsAdded = [];
                    //         this.goToStep('stage');
                    //     },
                    // },

                    mainBtn: {
                        label: 'Continue to next step',
                        action: () => {
                            this.injectRecipients();
                            this.goToStep('stage');
                        },
                    },
                },

                stage: {
                    title: 'Opportunity Stage',
                    secondaryBtn: {
                        label: 'Go Back',
                        action: () => {
                            this.recipientsAdded = [];
                            this.goToStep('contacts');
                        },
                    },

                    mainBtn: {
                        label: 'Continue',
                        action: () => {
                            this.saveChanges();
                            this.closePopup();
                        },
                    },
                },
            },
        };
    },

    computed: {
        contact() {
            return this.element.options.contact;
        },

        salesperson() {
            return this.element.options.salesperson;
        },
    },

    methods: {
        goToStep(step) {
            this.step = step;
        },

        closePopup() {
            this.$emit('close');
        },

        toggleCheckbox(propertyToToggle, refName) {
            this[propertyToToggle] = !this[propertyToToggle];
            this.$refs[refName].$refs.checkbox.checked = this[propertyToToggle];
        },

        toggleAppSwitch() {
            this.$refs.appSwitch.toggleCheck();
        },

        injectRecipients() {
            if (this.contactSelected) {
                this.recipientsAdded.push({
                    name: this.$helpers.getName(this.contact.FirstName, this.contact.LastName),
                    email: this.contact.Email,
                    profileImageUrl: this.contact.ProfileImageUrl,
                    recipientId: this.contact.Id,
                    type: 'Contact',
                });
            }

            if (this.salespersonSelected) {
                this.recipientsAdded.push({
                    name: this.$helpers.getName(
                        this.salesperson.FirstName,
                        this.salesperson.LastName,
                    ),
                    email: this.salesperson.Email,
                    profileImageUrl: this.salesperson.ProfileImageUrl,
                    recipientId: this.salesperson.Id,
                    type: 'User',
                });
            }
        },

        showOpportunityStages() {
            this.isChangingStageOnCompletion = !this.isChangingStageOnCompletion;
        },

        saveChanges() {
            const opportunity = {
                recipientsAdded: this.recipientsAdded,
                isChangingStageOnCompletion: this.isChangingStageOnCompletion,
                stageOnCompletion: this.opportunityStatus,
            };

            EventBus.$emit('opportunity:apply-linking', opportunity);
        },
    },
};
</script>

<style>
.link-opportunity-popup .title {
    text-align: center;
    margin-bottom: 30px;
}

.link-opportunity-popup .opportunity-participants .popup-details {
    font: normal normal normal 22px/37px Nunito;
}

.link-opportunity-popup .app-switch-value-text {
    display: none;
}

.link-opportunity-popup .opportunity-participants {
    display: flex;
    width: 100%;
    justify-content: space-around;
    gap: 44px;
    margin-bottom: 30px;
}

.link-opportunity-popup .opportunity-stage-wrapper {
    width: 100%;
    height: 276px;
}

.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage {
    padding: 24px 29px;
    align-items: flex-start;
    overflow: hidden;
    transition: height 0.3s ease-out;
    height: 115px;
}

.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage .app-switch-wrapper,
.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage .stage-description p {
    cursor: pointer;
}

.link-opportunity-popup
    .opportunity-stage-wrapper
    .opportunity-stage
    .app-switch-wrapper
    .app-switch {
    margin-top: 20px;
    width: 170px;
}

.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage .stage-description {
    display: flex;
    flex-direction: column;
}

.link-opportunity-popup
    .opportunity-stage-wrapper
    .opportunity-stage
    .stage-description
    .select-opportunity-stage
    label {
    font: normal normal 16px/11px Roboto;
    font-weight: bold;
    margin-bottom: 6px;
}

.link-opportunity-popup
    .opportunity-stage-wrapper
    .opportunity-stage
    .stage-description
    .select-opportunity-stage
    .board-opportunity-status-dropdown {
    width: 468px;
}

.link-opportunity-popup
    .board-opportunity-status-dropdown
    .v-input
    .v-input__control
    input::placeholder {
    color: #dcdbe0;
}

.link-opportunity-popup .opportunity-participants .participant,
.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage {
    border: 1px solid #e9e9ef;
    border-radius: 16px;
    display: flex;
}

.link-opportunity-popup .opportunity-participants .participant {
    width: 324px;
    height: 109px;
    align-items: center;
}

.link-opportunity-popup .opportunity-participants .participant.selected,
.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage.selected {
    border: 2px solid #2ada2b;
}

.link-opportunity-popup .opportunity-stage-wrapper .opportunity-stage.selected {
    height: 215px;
}

.link-opportunity-popup .opportunity-participants .participant .details-container {
    display: flex;
    width: 100%;
    gap: 12px;
    align-items: center;
    margin-left: 5px;
    font: normal normal 20px/36px Roboto;
}

.link-opportunity-popup
    .opportunity-participants
    .participant
    .details-container
    .checkbox-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: transparent;
    height: 48px;
    width: 48px;
}

.link-opportunity-popup .opportunity-participants .participant:hover {
    cursor: pointer;
}

.link-opportunity-popup
    .opportunity-participants
    .participant:hover
    .details-container
    .checkbox-wrapper {
    background: var(--color-fill-accent-skyblue-special-use); /*mass replace*/
}

.link-opportunity-popup
    .opportunity-participants
    .participant.participant.selected:hover
    .details-container
    .checkbox-wrapper {
    background: transparent;
}

.link-opportunity-popup
    .opportunity-participants
    .participant:hover
    .details-container
    .checkbox-wrapper
    .app-checkbox
    .checkmark {
    position: absolute;
    background: url('../../../../assets/images/SVG/checkbox-hovered.svg');
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: top left;
    height: 20px;
    width: 20px;
}

.link-opportunity-popup
    .opportunity-participants
    .participant
    .details-container
    .app-profile-image
    .background-image {
    height: 80px;
    width: 80px;
}

.link-opportunity-popup .opportunity-participants .participant .details-container .details {
    width: 150px;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.link-opportunity-popup .app-popup-actions .tertiary-btn {
    margin-right: 15px;
}

.link-opportunity-popup .app-popup-actions .tertiary-btn .fa.fa-arrow-left {
    margin-right: 8px;
}

.link-opportunity-popup .app-popup-actions .main-btn {
    width: 170px;
}

.link-opportunity-popup .app-popup-actions .main-btn.single {
    margin-left: 0;
    width: 216px;
}
</style>
